import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllTickets, updateTicket } from '../../../api/tickets';
import UserSelect from '../../common/UserSelect';
import './BetaTimelineView.scss';

const STATUS_COLORS = {
  pending: '#f59e0b',
  in_progress: '#3b82f6',
  completed: '#10b981',
};

const DAYS_TO_SHOW = 60;

// 日期字串加減天數
const addDaysToDateStr = (dateStr, days) => {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const LABEL_DEFAULT_WIDTH = 280;
const LABEL_MIN_WIDTH = 180;

const BetaTimelineView = ({ onNavigate }) => {
  const { refreshMyTickets, usersMapByEmail, projects, companyUsers } = useBetaData();
  const { viewingCompanyId } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOffset, setViewOffset] = useState(0);
  const [dragState, setDragState] = useState(null);
  const [labelWidth, setLabelWidth] = useState(LABEL_DEFAULT_WIDTH);
  const [isLabelResizing, setIsLabelResizing] = useState(false);
  // inclusive:預設全選。空陣列代表「一個都沒勾」= 空結果,不是「不篩選」。
  const [projectFilter, setProjectFilter] = useState([]);
  const projFilterInitRef = useRef(false);
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const labelResizeRef = useRef({ startX: 0, startWidth: 0 });
  const timelineRef = useRef(null);
  const headerScrollRef = useRef(null);
  const labelsRef = useRef(null);
  const scrollSyncSource = useRef(null);
  const dragStateRef = useRef(null);
  const projectDropdownRef = useRef(null);

  useEffect(() => {
    loadTickets();
  }, [viewingCompanyId]);

  const loadTickets = async () => {
    setLoading(true);
    const { data } = await getAllTickets({ companyId: viewingCompanyId });
    setTickets(data || []);
    setLoading(false);
  };

  // 篩選後的 tickets
  const filteredTickets = useMemo(() => {
    let result = tickets;
    // 專案篩選（Exclusive 模式：沒選 = 全顯示）
    // inclusive:全不勾 = 空結果(對齊其他檢視);projects 還沒載入時才視為不篩選
    if ((projects || []).length > 0) {
      result = result.filter((t) => projectFilter.includes(t.project_id));
    }
    // 指派人員篩選（Exclusive 模式）
    if (assigneeFilter.length > 0) {
      result = result.filter((t) => {
        const assignees = t.ticket_assignees || [];
        return assignees.some((a) => assigneeFilter.includes(a.user_id));
      });
    }
    return result;
  }, [tickets, projectFilter, assigneeFilter]);

  // 有日期的 ticket，按 start_date 排序
  const ticketsWithDates = useMemo(() => {
    return filteredTickets
      .filter((t) => t.start_date || t.end_date)
      .sort((a, b) => {
        const aDate = a.start_date || a.end_date;
        const bDate = b.start_date || b.end_date;
        return aDate.localeCompare(bDate);
      });
  }, [filteredTickets]);

  const ticketsWithoutDates = useMemo(() => {
    return filteredTickets.filter((t) => !t.start_date && !t.end_date);
  }, [filteredTickets]);

  // 計算時間範圍（支援月份偏移）
  const { startDate, dates } = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 14 + viewOffset * 30);
    start.setHours(0, 0, 0, 0);

    const dateArray = [];
    for (let i = 0; i < DAYS_TO_SHOW; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dateArray.push(d);
    }
    return { startDate: start, dates: dateArray };
  }, [viewOffset]);

  const dayWidth = 36;
  const todayIndex = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((today - startDate) / (1000 * 60 * 60 * 24));
  }, [startDate]);

  // 滾動到今天（如果在可視範圍內），否則滾到開頭
  useEffect(() => {
    if (!timelineRef.current) return;
    const scrollLeft = todayIndex >= 0 && todayIndex < DAYS_TO_SHOW
      ? Math.max(0, (todayIndex - 3) * dayWidth)
      : 0;
    timelineRef.current.scrollLeft = scrollLeft;
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = scrollLeft;
  }, [loading, todayIndex]);

  const getBarPosition = (ticketOrDates) => {
    const sd = ticketOrDates.start_date
      ? new Date(ticketOrDates.start_date)
      : ticketOrDates.end_date
      ? new Date(ticketOrDates.end_date)
      : null;
    const ed = ticketOrDates.end_date
      ? new Date(ticketOrDates.end_date)
      : ticketOrDates.start_date
      ? new Date(ticketOrDates.start_date)
      : null;

    if (!sd || !ed) return null;

    sd.setHours(0, 0, 0, 0);
    ed.setHours(0, 0, 0, 0);

    const startOff = Math.round((sd - startDate) / (1000 * 60 * 60 * 24));
    const endOff = Math.round((ed - startDate) / (1000 * 60 * 60 * 24));
    const left = startOff * dayWidth;
    const width = Math.max((endOff - startOff + 1) * dayWidth - 4, dayWidth - 4);

    return { left, width };
  };

  const formatDate = (d) => {
    const month = d.getMonth() + 1;
    const day = d.getDate();
    return `${month}/${day}`;
  };

  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
  const isToday = (d) => {
    const today = new Date();
    return d.toDateString() === today.toDateString();
  };

  const formatTicketDateRange = (ticket) => {
    const fmt = (dateStr) => {
      const [y, m, d] = dateStr.split('-');
      return `${y}/${parseInt(m)}/${parseInt(d)}`;
    };
    const s = ticket.start_date;
    const e = ticket.end_date;
    if (s && e) return `${fmt(s)} - ${fmt(e)}`;
    if (s) return `${fmt(s)} ~`;
    if (e) return `~ ${fmt(e)}`;
    return '';
  };

  const rangeLabel = useMemo(() => {
    if (dates.length === 0) return '';
    const first = dates[0];
    const last = dates[dates.length - 1];
    const fy = first.getFullYear();
    const fm = first.getMonth() + 1;
    const ly = last.getFullYear();
    const lm = last.getMonth() + 1;
    if (fy === ly) {
      return fm === lm ? `${fy}年 ${fm}月` : `${fy}年 ${fm}月 - ${lm}月`;
    }
    return `${fy}年 ${fm}月 - ${ly}年 ${lm}月`;
  }, [dates]);

  // ─── 拖曳邏輯 ───────────────────────────────
  const handleBarMouseDown = useCallback((e, ticket, type) => {
    e.preventDefault();
    e.stopPropagation();
    const state = {
      ticketId: ticket.id,
      projectId: ticket.project_id,
      type, // 'start' | 'end' | 'move'
      initialMouseX: e.clientX,
      initialStartDate: ticket.start_date,
      initialEndDate: ticket.end_date,
      currentStartDate: ticket.start_date,
      currentEndDate: ticket.end_date,
    };
    dragStateRef.current = state;
    setDragState(state);
  }, []);

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e) => {
      const ds = dragStateRef.current;
      if (!ds) return;

      const deltaX = e.clientX - ds.initialMouseX;
      const deltaDays = Math.round(deltaX / dayWidth);
      if (deltaDays === 0 && ds.currentStartDate === ds.initialStartDate) return;

      let newStart = ds.initialStartDate;
      let newEnd = ds.initialEndDate;

      if (ds.type === 'move') {
        newStart = ds.initialStartDate ? addDaysToDateStr(ds.initialStartDate, deltaDays) : null;
        newEnd = ds.initialEndDate ? addDaysToDateStr(ds.initialEndDate, deltaDays) : null;
      } else if (ds.type === 'start') {
        newStart = addDaysToDateStr(ds.initialStartDate, deltaDays);
        // 不讓 start 超過 end
        if (newEnd && newStart > newEnd) newStart = newEnd;
      } else if (ds.type === 'end') {
        newEnd = ds.initialEndDate
          ? addDaysToDateStr(ds.initialEndDate, deltaDays)
          : addDaysToDateStr(ds.initialStartDate, deltaDays);
        // 不讓 end 早於 start
        if (ds.initialStartDate && newEnd < ds.initialStartDate) {
          newEnd = addDaysToDateStr(ds.initialStartDate, deltaDays);
          if (newEnd < ds.initialStartDate) newEnd = ds.initialStartDate;
        }
        if (newStart && newEnd < newStart) newEnd = newStart;
      }

      const updated = { ...ds, currentStartDate: newStart, currentEndDate: newEnd };
      dragStateRef.current = updated;
      setDragState(updated);
    };

    const handleMouseUp = async () => {
      const ds = dragStateRef.current;
      dragStateRef.current = null;
      setDragState(null);
      if (!ds) return;

      const startChanged = ds.currentStartDate !== ds.initialStartDate;
      const endChanged = ds.currentEndDate !== ds.initialEndDate;
      if (!startChanged && !endChanged) return;

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ds.ticketId
            ? { ...t, start_date: ds.currentStartDate, end_date: ds.currentEndDate }
            : t
        )
      );

      const updates = {};
      if (startChanged) updates.start_date = ds.currentStartDate;
      if (endChanged) updates.end_date = ds.currentEndDate;

      const { error } = await updateTicket(ds.ticketId, updates);
      if (error) {
        // Rollback
        setTickets((prev) =>
          prev.map((t) =>
            t.id === ds.ticketId
              ? { ...t, start_date: ds.initialStartDate, end_date: ds.initialEndDate }
              : t
          )
        );
      } else {
        refreshMyTickets();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, refreshMyTickets]);

  // 取得 ticket 的有效日期（拖曳中使用拖曳狀態的日期）
  const getEffectiveDates = (ticket) => {
    if (dragState && dragState.ticketId === ticket.id) {
      return { start_date: dragState.currentStartDate, end_date: dragState.currentEndDate };
    }
    return { start_date: ticket.start_date, end_date: ticket.end_date };
  };

  // 取得主要負責人名稱
  const getPrimaryOwnerName = (ticket) => {
    const first = (ticket.ticket_assignees || [])[0];
    if (!first) return null;
    const email = first.profiles?.email?.toLowerCase();
    return email ? usersMapByEmail[email]?.name || first.profiles?.email : null;
  };

  // ─── 左側面板寬度拖曳 ─────────────────────────
  const handleLabelResizeStart = useCallback((e) => {
    e.preventDefault();
    labelResizeRef.current = { startX: e.clientX, startWidth: labelWidth };
    setIsLabelResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [labelWidth]);

  useEffect(() => {
    if (!isLabelResizing) return;

    const onMove = (e) => {
      const diff = e.clientX - labelResizeRef.current.startX;
      const maxWidth = window.innerWidth * 0.5;
      const newWidth = Math.min(Math.max(labelResizeRef.current.startWidth + diff, LABEL_MIN_WIDTH), maxWidth);
      setLabelWidth(newWidth);
    };

    const onUp = () => {
      setIsLabelResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isLabelResizing]);

  // ─── Scroll sync（header ↔ grid 水平，labels ↔ grid 垂直）───
  const handleGridScroll = useCallback(() => {
    if (scrollSyncSource.current === 'labels') {
      scrollSyncSource.current = null;
      return;
    }
    scrollSyncSource.current = 'grid';
    if (headerScrollRef.current && timelineRef.current) {
      headerScrollRef.current.scrollLeft = timelineRef.current.scrollLeft;
    }
    if (labelsRef.current && timelineRef.current) {
      labelsRef.current.scrollTop = timelineRef.current.scrollTop;
    }
  }, []);

  const handleLabelsScroll = useCallback(() => {
    if (scrollSyncSource.current === 'grid') {
      scrollSyncSource.current = null;
      return;
    }
    scrollSyncSource.current = 'labels';
    if (timelineRef.current && labelsRef.current) {
      timelineRef.current.scrollTop = labelsRef.current.scrollTop;
    }
  }, []);

  // 專案篩選 dropdown click outside
  useEffect(() => {
    if (!projectDropdownOpen) return;
    const handleClick = (e) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target)) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [projectDropdownOpen]);

  // projects 載入後預設全選一次(之後使用者的取消勾選不再被覆寫)
  useEffect(() => {
    if (projFilterInitRef.current) return;
    if ((projects || []).length > 0) {
      projFilterInitRef.current = true;
      setProjectFilter(projects.map((p) => p.id));
    }
  }, [projects]);

  // 全選 = 沒在篩選,所以不計入;有取消勾選才算一個作用中的篩選
  const activeFilterCount = ((projects || []).length > 0 && projectFilter.length < projects.length ? 1 : 0)
    + (assigneeFilter.length > 0 ? 1 : 0);
  const clearAllFilters = () => {
    setProjectFilter((projects || []).map((p) => p.id));
    setAssigneeFilter([]);
  };

  const toggleProject = (projectId) => {
    setProjectFilter((prev) =>
      prev.includes(projectId) ? prev.filter((v) => v !== projectId) : [...prev, projectId]
    );
  };

  if (loading) {
    return (
      <div className="beta-timeline-view">
        <div className="timeline-loading">載入中...</div>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="beta-timeline-view">
        <div className="timeline-empty">尚無 Ticket</div>
      </div>
    );
  }

  return (
    <div className={`beta-timeline-view ${dragState ? 'is-dragging' : ''}`}>
      <div className="timeline-toolbar">
        <div className="timeline-nav">
          <button className="nav-btn" onClick={() => setViewOffset((v) => v - 1)} title="上個月">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 2L4 7l5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className={`nav-btn today-btn ${viewOffset === 0 ? 'active' : ''}`}
            onClick={() => setViewOffset(0)}
          >
            今天
          </button>
          <button className="nav-btn" onClick={() => setViewOffset((v) => v + 1)} title="下個月">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 2l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="nav-range-label">{rangeLabel}</span>
        </div>

        <div className="timeline-filters">
          {/* 專案篩選 */}
          {(projects || []).length > 0 && (
            <div
              className={`filter-dropdown ${projectDropdownOpen ? 'open' : ''} ${(projects || []).length > 0 && projectFilter.length < projects.length ? 'active' : ''}`}
              ref={projectDropdownRef}
            >
              <button className="filter-trigger" onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}>
                專案
                {(projects || []).length > projectFilter.length && (
                  <span className="filter-badge">{projects.length - projectFilter.length}</span>
                )}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 4l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {projectDropdownOpen && (
                <div className="filter-panel">
                  <div className="filter-options">
                    {(projects || []).map((p) => (
                      <label key={p.id} className="filter-option">
                        <input
                          type="checkbox"
                          checked={projectFilter.includes(p.id)}
                          onChange={() => toggleProject(p.id)}
                        />
                        <span className="option-dot" style={{ backgroundColor: p.color || '#667eea' }} />
                        <span className="option-label">{p.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 指派人員篩選 */}
          <div className="filter-assignee-wrapper">
            <UserSelect
              values={assigneeFilter}
              onChange={setAssigneeFilter}
              users={companyUsers || []}
              placeholder="指派人員"
            />
            {assigneeFilter.length > 0 && (
              <span className="filter-assignee-badge">{assigneeFilter.length}</span>
            )}
          </div>

          {/* 清除篩選 */}
          {activeFilterCount > 0 && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              清除
              <span className="clear-badge">{activeFilterCount}</span>
            </button>
          )}
        </div>
      </div>
      {filteredTickets.length === 0 && activeFilterCount > 0 ? (
        <div className="timeline-empty">
          篩選條件下無 Ticket，
          <button className="clear-link" onClick={clearAllFilters}>清除篩選</button>
        </div>
      ) : (
      <div className={`timeline-container ${isLabelResizing ? 'is-resizing' : ''}`}>
        {/* Frozen header row */}
        <div className="timeline-header-row">
          <div className="label-header" style={{ width: labelWidth }}>Ticket</div>
          <div className="header-resize-spacer" />
          <div className="grid-header-wrapper" ref={headerScrollRef}>
            <div className="grid-header" style={{ width: dates.length * dayWidth }}>
              {dates.map((d, i) => (
                <div
                  key={i}
                  className={`header-cell ${isWeekend(d) ? 'weekend' : ''} ${isToday(d) ? 'today' : ''}`}
                  style={{ width: dayWidth }}
                >
                  <span className="day-label">{formatDate(d)}</span>
                  <span className="weekday">{['日', '一', '二', '三', '四', '五', '六'][d.getDay()]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="timeline-body">
          {/* Left: ticket names */}
          <div className="timeline-labels" ref={labelsRef} style={{ width: labelWidth }} onScroll={handleLabelsScroll}>
            {ticketsWithDates.map((ticket) => {
              const ownerName = getPrimaryOwnerName(ticket);
              return (
                <div
                  key={ticket.id}
                  className="label-row"
                  onClick={() => onNavigate?.('ticket', ticket.project_id, ticket.id, { ticketNo: ticket.ticket_no })}
                >
                  <span className="label-id">#{ticket.ticket_no}</span>
                  <span className="label-title">{ticket.title}</span>
                  <div className="label-meta">
                    {ownerName && <span className="label-owner">{ownerName}</span>}
                    <span className="label-date">{formatTicketDateRange(ticket)}</span>
                  </div>
                </div>
              );
            })}
            {ticketsWithoutDates.length > 0 && (
              <>
                <div className="label-divider">未排期</div>
                {ticketsWithoutDates.map((ticket) => {
                  const ownerName = getPrimaryOwnerName(ticket);
                  return (
                    <div
                      key={ticket.id}
                      className="label-row no-date"
                      onClick={() => onNavigate?.('ticket', ticket.project_id, ticket.id, { ticketNo: ticket.ticket_no })}
                    >
                      <span className="label-id">#{ticket.ticket_no}</span>
                      <span className="label-title">{ticket.title}</span>
                      {ownerName && <span className="label-owner">{ownerName}</span>}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Resize handle */}
          <div className="label-resize-handle" onMouseDown={handleLabelResizeStart} />

          {/* Right: timeline grid */}
          <div className="timeline-grid" ref={timelineRef} onScroll={handleGridScroll}>
            {/* Ticket bars */}
            {ticketsWithDates.map((ticket) => {
              const effective = getEffectiveDates(ticket);
              const pos = getBarPosition(effective);
              const color = STATUS_COLORS[ticket.status] || '#999';
              const isDragging = dragState?.ticketId === ticket.id;
              return (
                <div
                  key={ticket.id}
                  className="grid-row"
                  style={{ width: dates.length * dayWidth }}
                >
                  {/* Background columns */}
                  {dates.map((d, i) => (
                    <div
                      key={i}
                      className={`grid-cell ${isWeekend(d) ? 'weekend' : ''} ${isToday(d) ? 'today' : ''}`}
                      style={{ width: dayWidth }}
                    />
                  ))}
                  {/* Bar */}
                  {pos && (
                    <div
                      className={`ticket-bar ${isDragging ? 'dragging' : ''}`}
                      style={{
                        left: pos.left + 2,
                        width: pos.width,
                        backgroundColor: color,
                      }}
                      title={ticket.title}
                    >
                      {/* Left resize handle */}
                      <div
                        className="bar-handle handle-left"
                        onMouseDown={(e) => handleBarMouseDown(e, ticket, 'start')}
                      />
                      {/* Move area */}
                      <div
                        className="bar-move-area"
                        onMouseDown={(e) => handleBarMouseDown(e, ticket, 'move')}
                      >
                        <span className="bar-label">{ticket.title}</span>
                      </div>
                      {/* Right resize handle */}
                      <div
                        className="bar-handle handle-right"
                        onMouseDown={(e) => handleBarMouseDown(e, ticket, 'end')}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {/* No-date rows (empty) */}
            {ticketsWithoutDates.map((ticket) => (
              <div
                key={ticket.id}
                className="grid-row no-date-row"
                style={{ width: dates.length * dayWidth }}
              >
                {dates.map((d, i) => (
                  <div
                    key={i}
                    className={`grid-cell ${isWeekend(d) ? 'weekend' : ''} ${isToday(d) ? 'today' : ''}`}
                    style={{ width: dayWidth }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default BetaTimelineView;
